/**
 * Single source of truth for volleyball referee roles. Mirrors the
 * server's NominationRepo::ROLES + the 005 / 008 CHECK constraints.
 *
 *   - code       : machine identifier stored in nominations.role
 *   - label      : long form used in admin tables ("1st Referee")
 *   - short      : short form used in dense tables ("1st Ref")
 *   - abbrev     : FRVB nomination-sheet abbreviation ("1", "2", "E-S")
 */
export interface RefereeRoleDef {
    code: string;
    label: string;
    short: string;
    abbrev: string;
}

export const REFEREE_ROLES: RefereeRoleDef[] = [
    { code: 'supervisor',          label: 'Supervisor',               short: 'Supervisor',  abbrev: 'SUP' },
    { code: 'first_ref',           label: '1st Referee',              short: '1st Ref',     abbrev: '1' },
    { code: 'second_ref',          label: '2nd Referee',              short: '2nd Ref',     abbrev: '2' },
    { code: 'third_ref',           label: '3rd Referee',              short: '3rd Ref',     abbrev: '3' },
    { code: 'reserve_ref',         label: 'Reserve Referee',          short: 'Reserve Ref', abbrev: 'RR' },
    { code: 'video_challenge_ref', label: 'Video Challenge Referee',  short: 'VCR',         abbrev: 'VCR' },
    { code: 'e_scorer',            label: 'E-Scorer (electronic)',    short: 'E-Scorer',    abbrev: 'E-S' },
    { code: 'scorer',              label: 'Scorer',                   short: 'Scorer',      abbrev: 'S' },
    { code: 'asst_scorer',         label: 'Assistant Scorer',         short: 'Asst Scorer', abbrev: 'AS' },
    { code: 'reserve_scorer',      label: 'Reserve Scorer',           short: 'Reserve',     abbrev: 'RS' },
    { code: 'line_judge_1',        label: 'Line Judge 1',             short: 'Line 1',      abbrev: 'L1' },
    { code: 'line_judge_2',        label: 'Line Judge 2',             short: 'Line 2',      abbrev: 'L2' },
    { code: 'line_judge_3',        label: 'Line Judge 3',             short: 'Line 3',      abbrev: 'L3' },
    { code: 'line_judge_4',        label: 'Line Judge 4',             short: 'Line 4',      abbrev: 'L4' },
];

/**
 * Roles flagged as the federation's *core* crew. These are the rows
 * the MatchEditModal renders as a fixed grid (1st / 2nd / Scorer /
 * Asst Scorer + the line-judge slots). Everything else is optional
 * and rendered on the table preview only when assigned. Supervisor
 * is the senior official in this federation's convention.
 */
export const CORE_ROLE_CODES = [
    'first_ref', 'second_ref', 'scorer', 'asst_scorer',
];
export const OPTIONAL_ROLE_CODES = [
    'supervisor', 'third_ref', 'reserve_ref',
    'video_challenge_ref', 'e_scorer', 'reserve_scorer',
];

export const ROLE_CODES = REFEREE_ROLES.map(r => r.code);

export const ROLE_LABELS:  Record<string, string> = Object.fromEntries(REFEREE_ROLES.map(r => [r.code, r.label]));
export const ROLE_SHORT:   Record<string, string> = Object.fromEntries(REFEREE_ROLES.map(r => [r.code, r.short]));
export const ROLE_ABBREV:  Record<string, string> = Object.fromEntries(REFEREE_ROLES.map(r => [r.code, r.abbrev]));

/**
 * Render a referee name in the federation's compact "SURNAME F.M."
 * format used on nomination sheets. Example:
 *   { first_name: 'Ntaganzwa Ernest', last_name: 'Kubwimana' }
 *     → "KUBWIMANA N. E"
 *   { first_name: 'Evode', last_name: 'Giraneza' }
 *     → "GIRANEZA E."
 */
export function compactName(r: { first_name?: string | null; last_name?: string | null; name?: string | null }): string {
    const last  = (r.last_name || '').trim();
    const first = (r.first_name || '').trim();
    if (last && first) {
        const initials = first.split(/\s+/).filter(Boolean).map(w => w[0].toUpperCase() + '.').join(' ');
        return last.toUpperCase() + ' ' + initials;
    }
    if (last) return last.toUpperCase();
    if (first) return first;
    return (r.name || '').toUpperCase() || '—';
}
